// Copyright 2016 Sorama B.V.

import {
    autoinject,
    bindable,
    computedFrom,
    containerless,
    customElement,
    Disposable,
    observable,
    TaskQueue,
} from "aurelia-framework";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotParentBase } from "controls/plot/PlotParentBase";
import { StringExtensions } from "extensions/StringExtensions";
import { IExportable } from "libraries/Export/IExportable";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { SVGNode } from "libraries/SVG/SVGNode";
import { IColorMap } from "services/ColorMap/IColorMap";
import { Generic } from "units/Generic";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue } from "units/Unit";

@customElement("color-bar")
@containerless
@autoinject
export class ColorBar extends PlotParentBase implements IExportable {
    private static ShowTimeout = 100;
    private static HideTimeout = 0;
    private static ColorBarInstanceCounter = 0;
    private static SmallColorBarThreshold = 300; // px;

    private readonly resizeListener: ResizeListener;
    private readonly taskQueue: TaskQueue;

    //#region HTML Elements

    private colorbarElement: HTMLElement;
    private popupElement: HTMLElement;
    private partsContainer: HTMLElement;

    //#endregion

    //#region Fields

    private subscriptions: Disposable[];

    private mouseOver: boolean;
    private showTimeout = -1;
    private hideTimeout = -1;
    private expanded = false;

    private instanceNumber: number;

    private minInvalid = false;
    private maxInvalid = false;

    private ignoreMinUpdate = false;
    private ignoreMaxUpdate = false;

    @observable
    private focussed = false;

    @observable
    private fixedMinimumValue: number;
    @observable
    private fixedMaximumValue: number;

    public showSmallVariant = false;

    @computedFrom("showSmallVariant", "expandedModule")
    public get inputGroupSizeClass(): string {
        if (this.expandedModule) {
            return "col-xs-3";
        } else if (this.showSmallVariant) {
            return "col-xs-6";
        } else {
            return "col-xs-5";
        }
    }

    @computedFrom("showSmallVariant", "expandedModule")
    public get inputGroupOffsetClass(): string {
        if (this.expandedModule) {
            return "col-xs-offset-6";
        } else if (this.showSmallVariant) {
            // No offset
            return "";
        } else {
            return "col-xs-offset-2";
        }
    }

    //#endregion

    //#region Bindables

    @bindable
    public colormap: IColorMap;

    @bindable
    public style: string;

    @bindable
    public fixedMinimum: UnitValue;

    @bindable
    public fixedMaximum: UnitValue;

    @bindable
    public minimum: UnitValue = new UnitValue(0, Generic.unitless);

    @bindable
    public maximum: UnitValue = new UnitValue(1, Generic.unitless);

    @bindable({ defaultValue: Generic.unitless })
    public defaultUnit = Pressure.pascal;

    @bindable
    public expandedModule: boolean;

    @bindable
    public decimals: number;

    //#endregion

    //#region PlotParentBase Members

    public requestMeasure(force = false): void {
        // Measure the control
        const redraw = this.measureControl(force);
        if (!force && !redraw) return; // No change in measure, skip the update

        // Since we always enforce a square aspect ratio (since SVG linewidth = 1px is not supported everywhere) we need to measure the viewport as well
        this.measureViewPort();

        // Colormap needs to redraw on a resize
        this.updateColorMap();
    }

    public requestViewPort(): void {
        this.measureViewPort();
    }

    public roundToNearestBin(value: number, axis: string): number {
        return value;
    }

    //#endregion

    //#region Constructor and Initialisation

    constructor(resizeListener: ResizeListener, taskQueue: TaskQueue) {
        super();
        this.resizeListener = resizeListener;
        this.taskQueue = taskQueue;
        this.instanceNumber = ColorBar.ColorBarInstanceCounter++;
    }

    public bind(): void {
        // Add observers on the width and height
        this.subscriptions = [
            this.resizeListener.elementResizeEvent(this.controlContainer, () => this.requestMeasure()),
        ];
    }

    public attached(): void {
        // Subscribe input handlers
        this.addInputEventListener("mouseenter", this.mouseEnter);
        this.addInputEventListener("mouseleave", this.mouseLeave);

        this.popupElement.addEventListener("mouseenter", this.mouseEnter);
        this.popupElement.addEventListener("mouseleave", this.mouseLeave);

        if (!this.minimum || !this.maximum) {
            this.minimum = new UnitValue(0.0, Generic.unitless);
            this.maximum = new UnitValue(1.0, Generic.unitless);
        }

        // Wait for aurelia to finish layouting, then measure the control
        this.taskQueue.queueTask(() => {
            this.requestMeasure();
        });
    }

    public detached() {
        // Unsubscribe input handlers
        this.removeInputEventListener("mouseenter", this.mouseEnter);
        this.removeInputEventListener("mouseleave", this.mouseLeave);
        this.popupElement.removeEventListener("mouseenter", this.mouseEnter);
        this.popupElement.removeEventListener("mouseleave", this.mouseLeave);
    }

    public unbind(): void {
        while (this.subscriptions.length > 0) {
            this.subscriptions.pop().dispose();
        }
    }

    //#endregion

    //#region Change Handlers

    public focussedChanged(): void {
        if (this.focussed && this.hideTimeout !== -1) {
            window.clearTimeout(this.hideTimeout);
            this.hideTimeout = -1;
        }

        if (!this.focussed && !this.mouseOver && this.hideTimeout === -1) {
            this.hideTimeout = window.setTimeout(() => {
                this.hideTimeout = -1;
                this.expanded = false;
            }, ColorBar.HideTimeout);
        }
    }

    public colormapChanged(): void {
        this.updateColorMap();
    }

    public fixedMinimumValueChanged(
        newValue: number,
        oldValue: number,
        propertyName: string,
        stopPropagation: boolean
    ): void {
        if (this.ignoreMinUpdate) {
            this.ignoreMinUpdate = false;
            return;
        }

        if (this.fixedMinimumValue !== null && this.fixedMinimumValue !== undefined) {
            const minUnitValue = new UnitValue(this.fixedMinimumValue, this.minimum.unit);

            if (this.fixedMaximum && this.fixedMaximum.value <= minUnitValue.toUnit(this.fixedMaximum.unit)) {
                // Invalid value, input is larger than fixed maximum input
                this.minInvalid = true;
                return;
            }

            this.minInvalid = false;
            this.fixedMinimum = minUnitValue;
        } else {
            this.fixedMinimum = undefined;
            this.minInvalid = false;
        }

        // Trigger the fixed maximum changed
        if (!stopPropagation) this.fixedMaximumValueChanged(this.fixedMinimumValue, undefined, undefined, true);
        else this.measureViewPort();
    }

    public fixedMaximumValueChanged(
        newValue: number,
        oldValue: number,
        propertyName: string,
        stopPropagation: boolean
    ): void {
        if (this.ignoreMaxUpdate) {
            this.ignoreMaxUpdate = false;
            return;
        }

        if (this.fixedMaximumValue !== null && this.fixedMaximumValue !== undefined) {
            const maxUnitValue = new UnitValue(this.fixedMaximumValue, this.maximum.unit);

            if (this.fixedMinimum && this.fixedMinimum.value >= maxUnitValue.toUnit(this.fixedMinimum.unit)) {
                // Invalid value, input is larger than fixed maximum input
                this.maxInvalid = true;
                return;
            }

            this.maxInvalid = false;
            this.fixedMaximum = maxUnitValue;
        } else {
            this.fixedMaximum = undefined;
            this.maxInvalid = false;
        }

        // Trigger the fixed minimum changed
        if (!stopPropagation) this.fixedMinimumValueChanged(this.fixedMaximumValue, undefined, undefined, true);
        else this.measureViewPort();
    }

    public fixedMinimumChanged() {
        const newValue =
            this.fixedMinimum && !Number.isNaN(this.fixedMinimum.value) ? this.fixedMinimum.value : undefined;
        if (this.fixedMinimumValue !== newValue) this.ignoreMinUpdate = true;

        this.fixedMinimumValue = newValue;
    }

    public fixedMaximumChanged() {
        const newValue =
            this.fixedMaximum && !Number.isNaN(this.fixedMaximum.value) ? this.fixedMaximum.value : undefined;
        if (this.fixedMaximumValue !== newValue) this.ignoreMaxUpdate = true;

        this.fixedMaximumValue = newValue;
    }

    public minimumChanged(newValue: UnitValue, oldValue: UnitValue) {
        if (!oldValue) this.minimum = new UnitValue(0, Generic.unitless);

        if (newValue && oldValue && newValue.unit == oldValue.unit && newValue.value == oldValue.value) {
            // No actual change, ignore
            return;
        }

        if (
            newValue === null ||
            newValue === undefined ||
            typeof newValue.value !== "number" ||
            (this.maximum !== null && this.maximum !== undefined && !newValue.unit.isOfSameUnitFamily(this.defaultUnit))
        ) {
            this.minimum = oldValue;
            return;
        }

        this.measureViewPort();
    }

    public maximumChanged(newValue: UnitValue, oldValue: UnitValue) {
        if (!oldValue) this.maximum = new UnitValue(1, Generic.unitless);

        if (newValue && oldValue && newValue.unit == oldValue.unit && newValue.value == oldValue.value) {
            // No actual change, ignore
            return;
        }

        if (
            newValue === null ||
            newValue === undefined ||
            typeof newValue.value !== "number" ||
            (this.minimum !== null && this.minimum !== undefined && !newValue.unit.isOfSameUnitFamily(this.defaultUnit))
        ) {
            this.maximum = oldValue;
            return;
        }

        this.measureViewPort();
    }

    public defaultUnitChanged() {
        this.measureViewPort();
    }

    private updateColorMap(): void {
        if (!this.colormap || !this.size) return;

        const colorMap = this.colormap.toColorRange(256);
        let linGradient = "linear-gradient(to right";

        // Add stops
        for (let i = 0; i < colorMap.length; i++) {
            const color = colorMap[i];
            const r = Math.min(Math.max(Math.round(color.r * 255), 0), 255);
            const g = Math.min(Math.max(Math.round(color.g * 255), 0), 255);
            const b = Math.min(Math.max(Math.round(color.b * 255), 0), 255);

            linGradient += ",rgb(" + r + "," + g + "," + b + ")";
        }

        linGradient += ")";

        if (!this.colorbarElement) return;

        // Set css style on the colorbar div
        this.colorbarElement.setAttribute("style", "background:" + linGradient + ";width:100%;height:100%;");
    }

    private measureControl(force: boolean): boolean {
        if (
            !this.controlContainer ||
            (!force &&
                this.controlContainer.clientWidth === this.size.width &&
                this.controlContainer.clientHeight === this.size.height)
        )
            // Not initialised or no change, no need to measure
            return false;

        // Measure children
        const margin = new Margin(0, 0, 0, 0);

        this.plotParts.forEach((x) => {
            const partMargin = x.onMeasureMargin();

            margin.left = Math.max(margin.left, partMargin.left);
            margin.top = Math.max(margin.top, partMargin.top);
            margin.right = Math.max(margin.right, partMargin.right);
            margin.bottom = Math.max(margin.bottom, partMargin.bottom);
        });

        if (margin.left + margin.right > this.controlContainer.clientWidth) {
            margin.left = 0;
            margin.right = 0;
        }

        this.margin = margin;

        // Get the size from the containing div element
        this.size = new Size(this.controlContainer.clientWidth, this.controlContainer.clientHeight);
        this.showSmallVariant =
            this.size.width - this.margin.left - this.margin.right < ColorBar.SmallColorBarThreshold;

        // Update colormap
        this.colormapChanged();

        // Notify children of size and margin change
        this.plotParts.forEach((x) => {
            x.onSize(this.size, this.margin);
        });

        return true;
    }

    private measureViewPort(): void {
        let min = this.fixedMinimum ? this.fixedMinimum : this.minimum;
        let max = this.fixedMaximum ? this.fixedMaximum : this.maximum;

        if (
            !this.size ||
            this.size.width === 0 ||
            !min ||
            !max ||
            !min.unit.isOfSameUnitFamily(this.defaultUnit) ||
            !max.unit.isOfSameUnitFamily(this.defaultUnit)
        )
            return;

        let unit = this.minimum.unit;
        if (unit === Generic.unitless && this.defaultUnit !== Generic.unitless) {
            unit = this.defaultUnit;
            min = new UnitValue(min.value, unit);
            max = new UnitValue(max.value, unit);
        }

        let width = max.toUnit(unit) - min.toUnit(unit);
        if (width <= 0 || isNaN(width)) {
            // To prevent weird scaling
            min = new UnitValue(0, unit);
            max = new UnitValue(1, unit);
            width = 1;
        }

        this.viewPort = new Rect(min.toUnit(unit), 0, width, (width / this.size.width) * this.size.height);

        this.plotParts.forEach((x) => {
            x.onViewPortComputed(this.viewPort, this.viewPortBounds, new Pair<Unit>(unit, Generic.unitless));
        });
    }

    public copyValueToClipboard(clipboardValue: number): void {
        if (typeof this.decimals === "string" && <string>this.decimals === "scientific") {
            StringExtensions.copyToClipboard(clipboardValue.toExponential());
        } else {
            StringExtensions.copyToClipboard(clipboardValue.toFixed(this.decimals));
        }
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        const svgNode = new SVGNode("svg");

        // Update viewbox size to include margin
        svgNode.setAttribute(
            "viewBox",
            StringExtensions.format(
                "{0},{1},{2},{3}",
                (-this.margin.left).toFixed(0),
                (-this.margin.top).toFixed(0),
                this.size.width.toFixed(0),
                this.size.height.toFixed(0)
            )
        );

        svgNode.setAttribute("shape-rendering", "geometricPrecision");

        const svgSize = {
            width: this.size.width - this.margin.left - this.margin.right,
            height: this.size.height - this.margin.top - this.margin.bottom,
        };

        const time = new Date().getTime();
        const clipPathId = "svgcolorbarclip" + time;
        const gradientId = "colormapgradient" + time;

        // Generate a defs section with a clip path
        const defsNode = new SVGNode("defs");
        const clipPathNode = new SVGNode("clipPath");
        clipPathNode.setAttribute("id", clipPathId);

        let rectNode = new SVGNode("rect");
        rectNode.setAttribute("x", "0");
        rectNode.setAttribute("y", "0");
        rectNode.setAttribute("width", svgSize.width.toFixed(0));
        rectNode.setAttribute("height", svgSize.height.toFixed(0));

        clipPathNode.addChild(rectNode);
        defsNode.addChild(clipPathNode);

        const gradientNode = new SVGNode("linearGradient");
        gradientNode.setAttribute("id", gradientId);
        gradientNode.setAttribute("x1", "0%");
        gradientNode.setAttribute("y1", "0%");
        gradientNode.setAttribute("x2", "100%");
        gradientNode.setAttribute("y2", "0%");

        // Add all stops
        const colorMap = this.colormap.toColorRange(256);

        // Add stops
        for (let i = 0; i < colorMap.length; i++) {
            const color = colorMap[i];
            const r = Math.min(Math.max(Math.round(color.r * 255), 0), 255).toFixed(0);
            const g = Math.min(Math.max(Math.round(color.g * 255), 0), 255).toFixed(0);
            const b = Math.min(Math.max(Math.round(color.b * 255), 0), 255).toFixed(0);

            const stopNode = new SVGNode("stop");
            stopNode.setAttribute("offset", ((i / (colorMap.length - 1)) * 100).toFixed(3) + "%");
            stopNode.setAttribute("stop-color", StringExtensions.format("rgb({0},{1},{2})", r, g, b));

            gradientNode.addChild(stopNode);
        }

        defsNode.addChild(gradientNode);
        svgNode.addChild(defsNode);

        // Add the waterfall image nodes
        const gNode = new SVGNode("g");
        gNode.setAttribute("clip-path", "url(#" + clipPathId + ")");

        rectNode = new SVGNode("rect");
        rectNode.setAttribute("x", "0");
        rectNode.setAttribute("y", "0");
        rectNode.setAttribute("width", svgSize.width.toFixed(0));
        rectNode.setAttribute("height", svgSize.height.toFixed(0));
        rectNode.setAttribute("fill", "url(#" + gradientId + ")");

        gNode.addChild(rectNode);
        svgNode.addChild(gNode);

        // For each plot part, check if they implement exportToSvg
        const partPromises = this.plotParts.map((part) => {
            const exportablePart = <IExportable>(<unknown>part);
            if (!exportablePart.exportToSvg) return Promise.resolve(undefined);

            return exportablePart.exportToSvg().then((partSvgNode) => {
                if (partSvgNode) {
                    // Remove SVG parent and replace with g
                    const gNode = new SVGNode("g");
                    for (const child of partSvgNode.children) {
                        gNode.addChild(child);
                    }

                    return gNode;
                }
            });
        });

        return Promise.all(partPromises).then((nodes) => {
            for (const node of nodes.filter((x) => x)) {
                svgNode.addChild(node);
            }

            return svgNode;
        });
    }

    //#endregion

    //#region Mouse Handlers

    private mouseEnter = (): boolean => {
        if (this.mouseOver) return false;

        this.mouseOver = true;

        if (this.hideTimeout !== -1) {
            window.clearTimeout(this.hideTimeout);
            this.hideTimeout = -1;
        }

        this.showTimeout = window.setTimeout(() => {
            this.showTimeout = -1;
            this.expanded = true;
        }, ColorBar.ShowTimeout);

        return false;
    };

    private mouseLeave = (): boolean => {
        if (!this.mouseOver) return false;

        this.mouseOver = false;

        if (this.focussed) return false;

        if (this.showTimeout !== -1) {
            window.clearTimeout(this.showTimeout);
            this.showTimeout = -1;
        }

        this.hideTimeout = window.setTimeout(() => {
            this.hideTimeout = -1;
            this.expanded = false;
        }, ColorBar.HideTimeout);

        return false;
    };

    //#endregion
}
